import { defineStore } from 'pinia';
import Api from '@/modules/core/api/api.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { DocumentEndPoint } from '@/modules/document/enums/document-end-point.enum.ts';
import type { ElectronicSignatureRow } from '@/modules/document/adapters/electronic-signature.type.ts';
import { ElectronicSignatureAdapter } from '@/modules/document/adapters/electronic-signature.adapter.ts';

type DocSignaturesState = {
  items: ElectronicSignatureRow[];
  roles: string[];
  count: number | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: unknown;
  controller: AbortController | null;
};

export const useDocSignaturesStore = defineStore(StoreId.DocSignatures, {
  state: (): DocSignaturesState => ({
    items: [],
    roles: [],
    count: null,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    controller: null,
  }),
  getters: {
    list: (state) => state.items,
  },
  actions: {
    async fetch(params: Record<string, unknown>) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.isLoading = true;
      this.isLoadingMore = false;

      try {
        const response = await Api.post(
          DocumentEndPoint.GetSignatures,
          { doc_id: params.doc_id },
          controller.signal,
          false
        );

        if (this.controller !== controller) {
          return;
        }

        const table = ElectronicSignatureAdapter.mapList(response.data);
        this.items = table.rows;
        this.roles = table.roles;
        this.count = this.items.length;
        this.error = null;
      } catch (error) {
        if (this.controller !== controller) {
          return;
        }
        if (ErrorUtil.isForbidden(error) || ErrorUtil.isBusinessError(error)) {
          this.error = error;
          return;
        }
        if (ErrorUtil.isUnexpected(error)) {
          this.error = error;
          throw error;
        }
      } finally {
        if (this.controller === controller) {
          this.isLoading = false;
          this.controller = null;
        }
      }
    },
    async fetchMore() {},
    abort() {
      this.controller?.abort();
      this.isLoading = false;
      this.isLoadingMore = false;
      this.controller = null;
    },
    clear() {
      this.abort();
      this.items = [];
      this.roles = [];
      this.count = null;
      this.error = null;
      this.isLoadingMore = false;
    },
  },
});
